import React from "react";

// Os tres estados de uma conversa, num sitio so: a ordem daqui e a ordem do
// filtro da lista, a cor e a do ponto e a chave e a da traducao. Isto vivia em
// tres sitios, e um estado novo obrigava a lembrar os tres.
//
// As cores sao os tokens --color-status-*, e nao o mint e o amber da casa. A
// razao esta escrita no index.css e resume-se a isto: na lista o ponto passou a
// ser a UNICA marca de estado da linha, e uma marca que carrega significado
// precisa de 3:1. O mint da 2.00:1 sobre branco.
//
// "Resolvida" nao tem cor nem ponto: e o estado calmo e o da maioria das
// linhas, e um ponto em todas as linhas era exatamente o ruido que a terceira
// linha antiga fazia. A palavra continua la, na pastilha e no historico.
//
// `labelKey` descreve UMA conversa ("Aberta"); `filterKey` descreve o conjunto
// que o botao de filtro escolhe ("Abertas"). Em ingles sao a mesma palavra, em
// portugues nao, e o botao ao lado de "Todas" tem de concordar em numero.
const STATUS_META = {
  open: {
    color: "var(--color-status-open)",
    pillBg: "var(--color-status-open-bg)",
    labelKey: "conversationsStatusOpen",
    filterKey: "conversationsFilterOpen",
    dot: true,
  },
  resolved: {
    color: "var(--color-text-secondary)",
    pillBg: "var(--color-surface-raised)",
    labelKey: "conversationsStatusResolved",
    filterKey: "conversationsFilterResolved",
    dot: false,
  },
  handed_off: {
    color: "var(--color-status-handed)",
    pillBg: "var(--color-status-handed-bg)",
    labelKey: "conversationsStatusHandedOff",
    filterKey: "conversationsFilterHandedOff",
    dot: true,
  },
};

// Os estados que o filtro da lista oferece.
export const CONVERSATION_STATUSES = Object.keys(STATUS_META);

// Chaves de traducao do estado, partilhadas pela lista, pela transcricao e pelo
// painel do contacto. Um estado que o backend invente e que nao esteja aqui nao
// tem chave, e quem chama desenha o valor cru em vez de inventar uma palavra.
export const STATUS_KEYS = CONVERSATION_STATUSES.reduce((acc, status) => {
  acc[status] = STATUS_META[status].labelKey;
  return acc;
}, {});

// Chaves de traducao dos botoes de filtro da lista, uma por estado.
export const STATUS_FILTER_KEYS = CONVERSATION_STATUSES.reduce((acc, status) => {
  acc[status] = STATUS_META[status].filterKey;
  return acc;
}, {});

// Se este estado se marca com um ponto. Quem desenha uma linha da lista precisa
// de saber isto ANTES de desenhar, para nao reservar espaco a um ponto que nao
// vem.
export const statusHasDot = (status) => Boolean(STATUS_META[status]?.dot);

/**
 * O ponto ao lado da palavra. `corner` poe-o no canto de um avatar, com um anel
 * da cor da folha para se ler por cima do disco.
 *
 * @param {{ status: string, corner?: boolean }} props
 */
const ConversationStatusDot = ({ status, corner = false }) => {
  const meta = STATUS_META[status];
  if (corner && !meta?.dot) return null;

  return (
    <span
      aria-hidden="true"
      className={
        corner
          ? "absolute -bottom-px -right-px w-2.5 h-2.5 rounded-full ring-2 ring-[var(--color-surface)]"
          : "w-2 h-2 rounded-full shrink-0"
      }
      style={{ backgroundColor: meta ? meta.color : "var(--color-text-secondary)" }}
    />
  );
};

/**
 * O estado como pastilha, no cabecalho da transcricao. A cor e ao mesmo tempo o
 * fundo e o texto, portanto os tokens tiveram de passar 4.5:1 e nao so 3:1.
 *
 * @param {{ status: string, label: string }} props
 */
export const ConversationStatusPill = ({ status, label }) => {
  const meta = STATUS_META[status];
  return (
    <span
      className="inline-flex items-center gap-1.5 h-[22px] px-2 rounded-full text-xs font-medium shrink-0"
      style={{
        backgroundColor: meta ? meta.pillBg : "var(--color-surface-raised)",
        color: meta ? meta.color : "var(--color-text-secondary)",
      }}
    >
      {meta?.dot && <ConversationStatusDot status={status} />}
      {label}
    </span>
  );
};

export default ConversationStatusDot;
